/**
 * These helpers are built to add support to the final part of the Sign in with
 * Apple flow by moving the login logic to a GraphQL mutation.
 *
 * For the first part of the final flow, see
 * apps/khanacademy/src/shared-package/apple-util.js.
 *
 * We also calculate the continueUrl depending on different factors:
 *  - It's the first time the user is logged in.
 *  - We use classCodes to join a certain classroom.
 *  - Is the user a teacher.
 *
 * @see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/535757033/Sign+in+with+Apple
 */
import {t} from "@lingui/core/macro";
import gql from "graphql-tag";

import {
    joinClassroomsPostlogin,
    roleToUserRole,
    setAnalyticsCookies,
    type LoginTypeData,
    type Role,
} from "@repo/auth-shared";
import type {ApolloGqlClient} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";
import {redirect} from "@repo/urls";

import ApolloGqlError from "~/data-access/gql/apollo-gql-error.ts";

import type {
    linkAppleAccountMutationType,
    loginWithAppleAuthCodeAndStateType,
    reauthWithAppleMutationType,
} from "./__graphql-types__/index.ts";

export interface AppleUserData {
    email: string;
    firstName: string;
    lastName: string;
}

export const APPLE_LOGIN_MUTATION: GqlOperation<
    loginWithAppleAuthCodeAndStateType["response"],
    loginWithAppleAuthCodeAndStateType["variables"]
> = gqlOp(gql`
    mutation loginWithAppleAuthCodeAndState(
        $authCode: String!
        $state: String!
        $userData: AppleUserData
        $role: UserRole
    ) {
        loginWithAppleAuthCodeAndState(
            authCode: $authCode
            state: $state
            userData: $userData
            role: $role
        ) {
            error {
                code
                accessToken
            }
            user {
                id
                kaid
                canAccessDistrictsHomepage
                isTeacher
                hasUnresolvedInvitations
                preferredKaLocale {
                    id
                    kaLocale
                    status
                }
            }
            isFirstLogin
        }
    }
`);

export const getErrorMessageForError = (error?: {error?: string}): string => {
    switch (error?.error) {
        case "popup_blocked_by_browser":
            return t`Your browser blocked a popup window required for logging in. Please change your browser settings to allow popups from khanacademy.org.`;
        case "popup_closed_by_user":
            return t`You closed the popup window before logging in. If that was a mistake, try logging in again.`;
        default:
            return t`Error logging in. Please try again.`;
    }
};

// sync-start:oauth-shadows-apple 418000128 https://github.com/Khan/webapp/blob/master/services/users/resolvers/complete_sign_in_with_apple.go
/**
 * Verify if the current user is trying to claim an existing account, and if
 * that is the case, redirect to the login/oauth/verify page.
 *
 * @param {string} errorCode The error code returned from the login service.
 * @param {string} accessToken The access token returned by Sign in with Apple.
 */
function checkShadowsExistingAccountError(
    errorCode: string,
    accessToken: string,
) {
    if (errorCode === "SHADOWS_EXISTING_ACCOUNT") {
        redirect(
            `/login/oauth/verify?oauth_type=apple&apple_access_token=${accessToken}`,
        );
    }
}
// sync-end:oauth-shadows-apple

/**
 * Validates the Apple authCode and the state value generated in
 * encodeAppleLoginState.
 *
 * Also tries to join a classroom if a signupCode is provided.
 *
 * @param {string} options.authCode The authorization code provided by Apple.
 * @param {string} options.state The state that contains the continue_url and
 * also provides by encodeAppleLoginState to prevent XSRF attacks.
 * @param {string} options.userData The Apple user info.
 * @param {Array<string>} [options.signupCodes] An optional list of signupCodes
 * passed in the UI. e.g /join/ABC123.
 * @param {Role} [options.role] The role will be asigned to the user being
 * logged in.
 */
export const loginWithAppleAuthCodeAndState = async (
    gqlClient: ApolloGqlClient,
    options: {
        authCode: string;
        state: string;
        userData: AppleUserData | null | undefined;
        signupCodes?: Array<string>;
        role?: Role;
    },
): Promise<LoginTypeData> => {
    const {authCode, state, userData, signupCodes, role} = options;

    try {
        const result = await gqlClient.mutate(APPLE_LOGIN_MUTATION, {
            variables: {
                authCode,
                state,
                userData,
                role: roleToUserRole(role),
            },
        });

        const loginWithAppleAuthCodeAndState =
            result.data?.loginWithAppleAuthCodeAndState;

        if (loginWithAppleAuthCodeAndState?.error) {
            const {
                error: {code, accessToken},
            } = loginWithAppleAuthCodeAndState;

            if (accessToken) {
                checkShadowsExistingAccountError(code, accessToken);
            }

            // We pass the error code directly, so that code is mapped to an UI
            // readable message.
            throw new KAError(code, Errors.Internal);
        }

        // TODO(somewhatabstract): Data could be null.
        // @ts-expect-error - TS2339 - Property 'user' does not exist on type '{ user: LoginUserData; isFirstLogin: boolean; } | { error: { code: string; accessToken: string; }; } | undefined'. | TS2339 - Property 'isFirstLogin' does not exist on type '{ user: LoginUserData; isFirstLogin: boolean; } | { error: { code: string; accessToken: string; }; } | undefined'.
        const {user, isFirstLogin} = loginWithAppleAuthCodeAndState;

        const loginUserData: LoginTypeData = {
            user,
            isFirstLogin,
        };

        // Only call the joinClassrooms mutation if the user is logged in
        // and is passing signupCodes.
        if (signupCodes && signupCodes.length) {
            loginUserData.joinClassrooms = await joinClassroomsPostlogin(
                gqlClient,
                signupCodes,
            );
        }

        setAnalyticsCookies(isFirstLogin, user.kaid);

        return loginUserData;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }
        // Otherwise we throw a generic error.
        throw new KAError(
            "Failed to login with Sign in with Apple",
            Errors.Internal,
            {cause: error},
        );
    }
};

export const REAUTH_WITH_APPLE_MUTATION: GqlOperation<
    reauthWithAppleMutationType["response"],
    reauthWithAppleMutationType["variables"]
> = gqlOp(gql`
    mutation reauthWithAppleMutation($authCode: String!, $state: String!) {
        reauthWithApple(authCode: $authCode, state: $state) {
            error {
                code
                accessToken
            }
            user {
                id
                kaid
            }
        }
    }
`);

interface ReauthWithApple {
    user: {
        id: string;
        kaid: string;
    };
}

/**
 * Re-authenticate the current user with an Apple account.
 *
 * @param {string} authCode The authorization code provided by Apple.
 * @param {string} state The state provided by encodeAppleLoginState to prevent
 * XSRF attacks.
 * @returns {Promise<ReauthWithApple>} Whether we return an error or the
 * user data.
 */
export const reauthWithApple = async (
    gqlClient: ApolloGqlClient,
    authCode: string,
    state: string,
): Promise<ReauthWithApple> => {
    try {
        const result = await gqlClient.mutate(REAUTH_WITH_APPLE_MUTATION, {
            variables: {authCode, state},
        });

        const reauthWithApple = result.data?.reauthWithApple;

        if (reauthWithApple?.error) {
            const {
                error: {code, accessToken},
            } = reauthWithApple;

            if (accessToken) {
                checkShadowsExistingAccountError(code, accessToken);
            }

            throw new KAError(code, Errors.Internal);
        }

        return {
            // TODO(somewhatabstract): Data could be null.
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2339 - Property 'user' does not exist on type '{ user: { id: string; kaid: string; }; } | { error: { code: string; accessToken: string; }; }'.
            user: reauthWithApple.user,
        };
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }
        // Otherwise we throw a generic error.
        throw new KAError(
            "Failed to re-auth with Sign in with Apple",
            Errors.Internal,
        );
    }
};

export const LINK_APPLE_ACCOUNT_MUTATION: GqlOperation<
    linkAppleAccountMutationType["response"],
    linkAppleAccountMutationType["variables"]
> = gqlOp(gql`
    mutation linkAppleAccountMutation($authCode: String!, $state: String!) {
        linkAppleAccount(authCode: $authCode, state: $state) {
            error {
                code
                accessToken
            }
            user {
                id
                kaid
            }
        }
    }
`);

interface LinkWithApple {
    user: {
        id: string;
        kaid: string;
    };
}

/**
 * Link an Apple account to the current user.
 *
 * @param {string} options.authCode The authorization code provided by Apple.
 * @param {string} options.state The state provided by encodeAppleLoginState to
 * prevent XSRF attacks.
 * @returns {Promise<ReauthWithApple>} Whether we return an error or the user
 * data.
 */
export const linkAppleAccount = async (
    gqlClient: ApolloGqlClient,
    options: {
        authCode: string;
        state: string;
    },
): Promise<LinkWithApple> => {
    try {
        const result = await gqlClient.mutate(LINK_APPLE_ACCOUNT_MUTATION, {
            variables: options,
        });

        const linkAppleAccount = result.data?.linkAppleAccount;

        if (linkAppleAccount?.error) {
            const {
                error: {code, accessToken},
            } = linkAppleAccount;

            if (accessToken) {
                checkShadowsExistingAccountError(code, accessToken);
            }

            throw new KAError(code, Errors.Internal);
        }

        return {
            // TODO(somewhatabstract): Data could be null.
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2339 - Property 'user' does not exist on type '{ user: { id: string; kaid: string; }; } | { error: { code: string; accessToken: string; }; }'.
            user: linkAppleAccount.user,
        };
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError && !(error instanceof ApolloGqlError)) {
            throw error;
        }
        // Otherwise we throw a generic error.
        throw new KAError("Failed to link an Apple account", Errors.Internal);
    }
};
